import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Autocomplete, Button, Chip, Paper, TextField, Tooltip, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import { createParameter, getAllParameters } from "../../../services/usecase";
import { setNoAlertStatus } from "../../../helpers/storage";
import { livisAlert } from "../../../helpers/alert";
import { doesUserHasAccess } from "../../../helpers/utils";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Truncate a label to `max` chars with ellipsis */
const truncateLabel = (label: string, max: number): string =>
  label.length > max ? `${label.slice(0, max)}…` : label;

// ─── Component ────────────────────────────────────────────────────────────────

const LivisChipAutocomplete = (props: any) => {
  const { t } = useTranslation();
  const {
    label_name,
    name,
    list,
    setList,
    sugesstedParameters,
    setSugesstedParameters,
    set_form_value,
    type,
    defaultSuggestion,
    disableApiFetch = false,
    otherSelectedList = [],
    otherType = "",
    isCustomModelUpload = false,
    // ── New props (only active when explicitly passed) ──
    maxVisibleChips,   // number | undefined — if set, collapse overflow into "+N" chip
    truncateAt,        // number | undefined — if set, truncate chip labels at N chars
  } = props;

  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const [isFetched, setIsFetched] = useState<any>(false);

  const splitAndTrimValues = (value: string): string[] =>
    value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

  useEffect(() => {
    setError("");
    if (inputValue.length >= 2 && !disableApiFetch) {
      fetchOptions(inputValue);
    } else {
      const suggestions = filterTags(defaultSuggestion?.[type], list);
      setSugesstedParameters(suggestions);
    }
  }, [inputValue, disableApiFetch]);

  const fetchOptions = useCallback(
    debounce(async (query: string) => {
      setIsFetched(false);
      const payload = { parameter: query };
      const hasAccess = doesUserHasAccess(124);
      const response = await getAllParameters(payload);
      const search_list = response?.data?.data;
      const result = filterTags(search_list, list);
      setIsFetched(true);
      setSugesstedParameters(result);
    }, 500),
    []
  );

  const filterTags = (allList: Array<string>, selectedList: Array<string>) => {
    const result = allList?.filter((tag: any) => !selectedList?.includes(tag));
    return result;
  };

  const handleAddCustomParameter = (params: any) => {
    const value = inputValue.trim();
    const payload = { parameter: value };
    setNoAlertStatus(true);

    if (value.length >= 1) {
      if (disableApiFetch) {
        if (isCustomModelUpload) {
          if (otherSelectedList?.includes(value)) {
            const msgType = otherType || (type === "features" ? "defect" : "feature");
            livisAlert(`${value} ${t("is already added as")} ${msgType}`, "warning");
            return;
          }
          const isInSuggestions = sugesstedParameters?.includes(value);
          if (!isInSuggestions) {
            livisAlert(`${t("Cannot add new parameters for uploaded model")}`, "error");
            return;
          }
          handleSelectTag(value);
        } else {
          splitAndTrimValues(value).forEach((item) => {
            handleSelectTag(item);
          });
        }
      } else {
        handleSelectTag(value);
        const hasAccess = doesUserHasAccess(123);
        createParameter(payload);
      }
    } else {
      setError("Please enter at least 1 character");
    }

    setSugesstedParameters(defaultSuggestion?.[type]);
  };

  const handleSelectTag = (value: string) => {
    const option: any = value.trim();

    if (disableApiFetch && isCustomModelUpload && otherSelectedList?.includes(option)) {
      const msgType = otherType || (type === "features" ? "defect" : "feature");
      livisAlert(`${option} is already added as ${msgType}`, "warning");
      return;
    }

    if (disableApiFetch && !isCustomModelUpload && otherSelectedList?.includes(option)) {
      const msgType = otherType || (type === "features" ? "defect" : "feature");
      livisAlert(`${option} ${t("is already added as")} ${msgType}`, "warning");
      return;
    }

    if (list?.includes(option)) {
      if (type === "defects") {
        livisAlert(`${t("Selected defect already exists in the list")}`, "warning");
        return;
      } else if (type === "features") {
        livisAlert(`${t("Selected feature already exists in the list")}`, "warning");
        return;
      }
    }

    const selected_list = [...list, option];
    setList(selected_list);

    const option_list_copy = [...sugesstedParameters];
    option_list_copy.splice(option_list_copy.indexOf(option), 1);
    setSugesstedParameters(option_list_copy);

    set_form_value(name, selected_list);
    setInputValue("");
  };

  const handleDeleteListTag = (data: any) => {
    const list_copy = [...list];
    list_copy.splice(list_copy.indexOf(data), 1);
    setList(list_copy);

    if (defaultSuggestion?.[type]?.includes(data)) {
      setSugesstedParameters([...sugesstedParameters, data]);
    }

    set_form_value(name, list_copy);
  };

  // ── Chip rendering ─────────────────────────────────────────────────────────
  /**
   * Renders the selected chips.
   *
   * When `maxVisibleChips` is set:
   *   - Show the first N chips normally.
   *   - Collapse the remainder into a single "+M more" Tooltip chip.
   *
   * When `truncateAt` is set:
   *   - Chip labels longer than N chars are truncated with "…".
   *   - A MUI Tooltip shows the full name on hover.
   */
  const renderChips = () =>
    list.map((data: any, index: number) => {
      // Overflow collapse — hide chips beyond the visible limit
      if (maxVisibleChips !== undefined && index === maxVisibleChips) {
        const hidden      = list.slice(maxVisibleChips);
        const tooltipBody = hidden.join(", ");
        return (
          <Tooltip
  key="overflow-chip"
  title={
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", padding: "4px", maxWidth: "260px" }}>
      {hidden.map((item: string, i: number) => (
        <Chip
          key={i}
          label={item}
          size="small"
          className={`livis-autocomplete-parameter-${type}`}
          sx={{
            height: "20px",
            fontSize: "11px",
            //           backgroundColor: type === "defects" ? "rgba(211,47,47,0.85)" : "rgba(46,125,50,0.85)",
           // color: "#fff",
            "& .MuiChip-label": {
              padding: "0 6px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "120px",
            },
          }}
        />
      ))}
    </Box>
  }
  arrow
  placement="bottom"
  PopperProps={{
    modifiers: [
      { name: "preventOverflow", options: { boundary: "window" } },
      { name: "flip", options: { fallbackPlacements: ["top", "right", "left"] } },
    ],
  }}
>
            <Chip
              className={`livis-autocomplete-parameter-${type} livis-chip-overflow`}
              label={`+${hidden.length}`}
              size="small"
              style={{ cursor: "default", fontWeight: 600 }}
            />
          </Tooltip>
        );
      }

      // Don't render chips beyond the overflow chip
      if (maxVisibleChips !== undefined && index > maxVisibleChips) return null;

      // Label truncation
      const shouldTruncate = truncateAt !== undefined && data.length > truncateAt;
      const displayLabel   = shouldTruncate ? truncateLabel(data, truncateAt!) : data;

      const chipNode = (
        <Chip
          className={`livis-autocomplete-parameter-${type}`}
          key={`${data}-tag-${index}`}
          label={displayLabel}
          onDelete={() => handleDeleteListTag(data)}
          deleteIcon={
            <CloseIcon
              tabIndex={0}
              onKeyDown={(e: any) => {
                if (e.key === "Enter") handleDeleteListTag(data);
              }}
            />
          }
          tabIndex={-1}
        />
      );

      return shouldTruncate ? (
        <Tooltip key={`${data}-tag-${index}`} title={data} arrow placement="top">
          {/* Tooltip needs a single DOM child, wrap chip in span */}
          <span style={{ display: "inline-flex" }}>{chipNode}</span>
        </Tooltip>
      ) : (
        chipNode
      );
    });

  return (
    <>
      <Autocomplete
        value={list}
        inputValue={inputValue}
        onChange={(event, newValue) => {
          if (disableApiFetch) {
            const added = (newValue || []).filter((v: any) => !list?.includes(v));
            if (added.length > 0) {
              if (isCustomModelUpload) {
                if (added.find((v: any) => otherSelectedList?.includes((v || "").toString().trim()))) {
                  const val = added.find((v: any) => otherSelectedList?.includes((v || "").toString().trim()));
                  const msgType = otherType || (type === "features" ? "defect" : "feature");
                  livisAlert(`${val} is already added as ${msgType}`, "warning");
                  setInputValue("");
                  return;
                }
                const invalid = added.find(
                  (v: any) => !sugesstedParameters?.includes((v || "").toString().trim())
                );
                if (invalid) {
                  livisAlert("Cannot add new parameters for uploaded model", "error");
                  setInputValue("");
                  return;
                }
              } else {
                if (added.find((v: any) => otherSelectedList?.includes((v || "").toString().trim()))) {
                  const val = added.find((v: any) => otherSelectedList?.includes((v || "").toString().trim()));
                  const msgType = otherType || (type === "features" ? "defect" : "feature");
                  livisAlert(`${val} is already added as ${msgType}`, "warning");
                  setInputValue("");
                  return;
                }
              }
            }
          }
          setList(newValue);
          set_form_value(name, newValue);
          setInputValue("");
        }}
        onInputChange={(event, newValue) => {
          setInputValue(newValue);
        }}
        multiple
        id="tags-filled"
        options={[]}
        defaultValue={[]}
        freeSolo
        disableClearable
        renderTags={() => renderChips()}
        tabIndex={-1}
        renderInput={(params: any) => (
          <>
            <TextField
              {...params}
              variant="standard"
              margin="normal"
              fullWidth
              label={label_name}
              type="text"
              className={`livis-input-field-${type}`}
              InputLabelProps={{
                className: "livis-input-field-component-label",
              }}
              onChange={(e) => {
                setInputValue(e.target.value);
              }}
              inputProps={{
                ...params.inputProps,
                onKeyDown: (e) => {
                  if (e.key === "Enter") {
                    handleAddCustomParameter(params);
                  }
                },
              }}
              value={inputValue}
            />
            <small className="livis-text-danger">{error && t(`${error}`)}</small>

            {disableApiFetch && isCustomModelUpload && sugesstedParameters.length > 0 && (
              <div className="livis-no-data-found-placeholder">
                {t("Available Classes from the uploaded model")}
              </div>
            )}

            <div>
              {sugesstedParameters?.map((option: any, index: number) => (
                <Button
                  key={`${option}-option-${index + 1}`}
                  className="livis-autocomplete-chip-btn"
                  variant="outlined"
                  onClick={() => handleSelectTag(option)}
                >
                  <span className="livis-autocomplete-chip-btn-text">{option}</span>
                </Button>
              ))}
              {!disableApiFetch && !isFetched && sugesstedParameters.length === 0 && (
                <div className="livis-no-data-found-placeholder">
                  {t("Type Something To Show Recommendations")}
                </div>
              )}
              {isFetched && sugesstedParameters.length === 0 && (
                <div className="livis-no-data-found-placeholder">{t("No Suggestions Found")}</div>
              )}
            </div>
          </>
        )}
      />
    </>
  );
};

LivisChipAutocomplete.propTypes = {
  label_name:             PropTypes.string,
  name:                   PropTypes.string,
  list:                   PropTypes.array,
  setList:                PropTypes.func,
  sugesstedParameters:    PropTypes.array,
  setSugesstedParameters: PropTypes.func,
  set_form_value:         PropTypes.func,
  fetchListOptions:       PropTypes.func,
  type:                   PropTypes.string,
  defaultSuggestion:      PropTypes.any,
  disableApiFetch:        PropTypes.bool,
  otherSelectedList:      PropTypes.array,
  otherType:              PropTypes.string,
  isCustomModelUpload:    PropTypes.bool,
  maxVisibleChips:        PropTypes.number,
  truncateAt:             PropTypes.number,
};

LivisChipAutocomplete.defaultProps = {
  sugesstedParameters:    [],
  type:                   "",
  disableApiFetch:        false,
  otherSelectedList:      [],
  otherType:              "",
  isCustomModelUpload:    false,
  maxVisibleChips:        undefined,
  truncateAt:             undefined,
};

export default LivisChipAutocomplete;